import {
  BingoCard,
  CreateLobbyPayload,
  CreateLobbyResponse,
  JoinLobbyPayload,
  JoinLobbyResponse,
  UpdateBingoPayload,
  BanPlayerPayload,
  KickPlayerPayload,
  WordClickPayload,
  WebSocketMessage,
  GetLobbyInfoPayload,
  GetLobbyInfoResponse,
  GetPlayerCardPayload,
  GetPlayerCardResponse,
  UpdateObsSettingsPayload,
  GetObsSettingsPayload,
  ObsSettingsResponse,
} from "../types";

type MessageHandler = (data: WebSocketMessage) => void;

class WebSocketService {
  private socket: WebSocket | null = null;
  private messageHandlers: Set<MessageHandler> = new Set();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private connectPromise: Promise<void> | null = null;

  // Dynamic WebSocket URL: wss for HTTPS, ws for HTTP, use current host
  wsProtocol = window.location.protocol === "https:" ? "wss" : "ws";
  wsHost = (() => {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") {
      return "localhost:8080";
    }
    return window.location.host;
  })();
  ws = new WebSocket(`${this.wsProtocol}://${this.wsHost}/ws`);

  connect(): Promise<void> {
    console.error("WebSocketService: connect called", this.ws);
    // If already connected, resolve immediately
    if (this.socket?.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    // If already connecting, return the existing promise
    if (
      this.connectPromise &&
      this.socket?.readyState === WebSocket.CONNECTING
    ) {
      return this.connectPromise;
    }

    // Close existing connection if in closing state
    if (this.socket && this.socket.readyState !== WebSocket.CLOSED) {
      this.socket.close();
    }

    this.connectPromise = new Promise((resolve, reject) => {
      try {
        this.socket = new WebSocket(`${this.wsProtocol}://${this.wsHost}/ws`);

        this.socket.onopen = () => {
          console.log("WebSocket connected");
          this.reconnectAttempts = 0;
          this.connectPromise = null;
          resolve();
        };

        this.socket.onmessage = (event) => {
          try {
            const data: WebSocketMessage = JSON.parse(event.data);
            this.messageHandlers.forEach((handler) => handler(data));
          } catch (error) {
            console.error("Failed to parse WebSocket message:", error);
          }
        };

        this.socket.onclose = () => {
          console.log("WebSocket disconnected");
          this.connectPromise = null;
          this.attemptReconnect();
        };

        this.socket.onerror = (error) => {
          console.error("WebSocket error:", error);
          this.connectPromise = null;
          reject(error);
        };
      } catch (error) {
        this.connectPromise = null;
        reject(error);
      }
    });

    return this.connectPromise;
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      setTimeout(() => {
        this.connect().catch(() => {
          // Reconnection failed, will try again
        });
      }, this.reconnectDelay * this.reconnectAttempts);
    }
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  subscribe(handler: MessageHandler): () => void {
    this.messageHandlers.add(handler);
    return () => {
      this.messageHandlers.delete(handler);
    };
  }

  private async send<T>(type: string, payload: T): Promise<void> {
    // Try to connect if not connected
    if (this.socket?.readyState !== WebSocket.OPEN) {
      try {
        await this.connect();
      } catch (error) {
        console.error("Failed to connect WebSocket for send:", error);
        return;
      }
    }

    if (this.socket?.readyState === WebSocket.OPEN) {
      const message = JSON.stringify({ type, payload });
      this.socket.send(message);
    } else {
      console.error(
        "WebSocket is not connected after connect attempt, readyState:",
        this.socket?.readyState,
      );
    }
  }

  createLobby(payload: CreateLobbyPayload): Promise<CreateLobbyResponse> {
    return new Promise((resolve, reject) => {
      const unsubscribe = this.subscribe((message) => {
        if (message.type === "create_lobby") {
          unsubscribe();
          resolve(message.payload as CreateLobbyResponse);
        } else if (message.type === "error") {
          unsubscribe();
          reject(new Error((message.payload as { message: string }).message));
        }
      });

      this.send("create_lobby", payload);

      // Timeout after 10 seconds
      setTimeout(() => {
        unsubscribe();
        reject(new Error("Create lobby request timed out"));
      }, 10000);
    });
  }

  joinLobby(payload: JoinLobbyPayload): Promise<JoinLobbyResponse> {
    return new Promise((resolve, reject) => {
      // Make sure we're connected first
      if (!this.isConnected()) {
        reject(new Error("WebSocket is not connected"));
        return;
      }

      const unsubscribe = this.subscribe((message) => {
        if (message.type === "join_lobby") {
          unsubscribe();
          resolve(message.payload as JoinLobbyResponse);
        } else if (message.type === "error") {
          unsubscribe();
          reject(new Error((message.payload as { message: string }).message));
        }
      });

      this.send("join_lobby", payload);

      // Timeout after 10 seconds
      setTimeout(() => {
        unsubscribe();
        reject(new Error("Join lobby request timed out"));
      }, 10000);
    });
  }

  updateBingo(payload: UpdateBingoPayload): void {
    this.send("update_bingo", payload);
  }

  banPlayer(payload: BanPlayerPayload): void {
    this.send("ban_player", payload);
  }

  kickPlayer(payload: KickPlayerPayload): void {
    this.send("kick_player", payload);
  }

  sendWordClick(payload: WordClickPayload): void {
    this.send("word_click", payload);
  }

  getLobbyInfo(payload: GetLobbyInfoPayload): Promise<GetLobbyInfoResponse> {
    return new Promise((resolve, reject) => {
      if (!this.isConnected()) {
        reject(new Error("WebSocket is not connected"));
        return;
      }

      const unsubscribe = this.subscribe((message) => {
        if (message.type === "get_lobby_info") {
          unsubscribe();
          resolve(message.payload as GetLobbyInfoResponse);
        } else if (message.type === "error") {
          unsubscribe();
          reject(new Error((message.payload as { message: string }).message));
        }
      });

      this.send("get_lobby_info", payload);

      setTimeout(() => {
        unsubscribe();
        reject(new Error("Get lobby info request timed out"));
      }, 10000);
    });
  }

  getPlayerCard(payload: GetPlayerCardPayload): Promise<GetPlayerCardResponse> {
    return new Promise((resolve, reject) => {
      if (!this.isConnected()) {
        reject(new Error("WebSocket is not connected"));
        return;
      }

      const unsubscribe = this.subscribe((message) => {
        if (message.type === "get_player_card") {
          unsubscribe();
          resolve(message.payload as GetPlayerCardResponse);
        } else if (message.type === "error") {
          unsubscribe();
          reject(new Error((message.payload as { message: string }).message));
        }
      });

      this.send("get_player_card", payload);

      setTimeout(() => {
        unsubscribe();
        reject(new Error("Get player card request timed out"));
      }, 10000);
    });
  }

  updateObsSettings(payload: UpdateObsSettingsPayload): void {
    this.send("update_obs_settings", payload);
  }

  getObsSettings(payload: GetObsSettingsPayload): Promise<ObsSettingsResponse> {
    return new Promise((resolve, reject) => {
      if (!this.isConnected()) {
        reject(new Error("WebSocket is not connected"));
        return;
      }

      const unsubscribe = this.subscribe((message) => {
        if (message.type === "obs_settings") {
          unsubscribe();
          resolve(message.payload as ObsSettingsResponse);
        } else if (message.type === "error") {
          unsubscribe();
          reject(new Error((message.payload as { message: string }).message));
        }
      });

      this.send("obs_settings", payload);

      setTimeout(() => {
        unsubscribe();
        reject(new Error("Get OBS settings request timed out"));
      }, 10000);
    });
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }
}

// Export singleton instance
export const wsService = new WebSocketService();

// Utility function to shuffle array (for randomizing bingo cards)
export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Utility function to generate a local bingo card from texts
export function generateLocalCard(
  gridSize: number,
  texts: string[],
): BingoCard {
  const totalCells = gridSize * gridSize;
  const shuffledTexts = shuffleArray(texts);

  const cells = shuffledTexts.slice(0, totalCells).map((text, index) => ({
    id: `cell-${index}`,
    text,
    marked: false,
  }));

  return {
    gridSize,
    cells,
  };
}

// Utility to check for all bingos and return winning cell indices
export function checkAllBingos(
  cells: { marked: boolean }[],
  gridSize: number,
): number[][] {
  const allBingos: number[][] = [];

  // Check rows
  for (let row = 0; row < gridSize; row++) {
    let rowComplete = true;
    const indices: number[] = [];
    for (let col = 0; col < gridSize; col++) {
      const idx = row * gridSize + col;
      indices.push(idx);
      if (!cells[idx].marked) {
        rowComplete = false;
        break;
      }
    }
    if (rowComplete) allBingos.push(indices);
  }

  // Check columns
  for (let col = 0; col < gridSize; col++) {
    let colComplete = true;
    const indices: number[] = [];
    for (let row = 0; row < gridSize; row++) {
      const idx = row * gridSize + col;
      indices.push(idx);
      if (!cells[idx].marked) {
        colComplete = false;
        break;
      }
    }
    if (colComplete) allBingos.push(indices);
  }

  // Check main diagonal
  let diagComplete = true;
  let diagIndices: number[] = [];
  for (let i = 0; i < gridSize; i++) {
    const idx = i * gridSize + i;
    diagIndices.push(idx);
    if (!cells[idx].marked) {
      diagComplete = false;
      break;
    }
  }
  if (diagComplete) allBingos.push(diagIndices);

  // Check anti-diagonal
  let antiDiagComplete = true;
  let antiDiagIndices: number[] = [];
  for (let i = 0; i < gridSize; i++) {
    const idx = i * gridSize + (gridSize - 1 - i);
    antiDiagIndices.push(idx);
    if (!cells[idx].marked) {
      antiDiagComplete = false;
      break;
    }
  }
  if (antiDiagComplete) allBingos.push(antiDiagIndices);

  return allBingos;
}

// Utility to check for bingo and return winning cell indices (backwards compatibility)
export function checkBingo(
  cells: { marked: boolean }[],
  gridSize: number,
): number[] | null {
  const allBingos = checkAllBingos(cells, gridSize);
  return allBingos.length > 0 ? allBingos[0] : null;
}
